import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { 
  FileText, 
  Printer,
  Save,
  Trash2,
  CheckCircle,
  AlertCircle,
  Shield,
  Settings,
  ClipboardList,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const AfterActionReportSection = ({ currentExercise, theme, toast: parentToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null
  const [agencyCoordinationEntries, setAgencyCoordinationEntries] = useState([]);
  const [statusSummaryEntries, setStatusSummaryEntries] = useState([]);
  const [lessonsLearnedEntries, setLessonsLearnedEntries] = useState([]);
  const [hierarchicalObjectives, setHierarchicalObjectives] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [resources, setResources] = useState([]);
  const [commsLogs, setCommsLogs] = useState([]);
  const [costUnitEntries, setCostUnitEntries] = useState([]);
  const [scenarioEntries, setScenarioEntries] = useState([]);
  const [incidentTrackerEntries, setIncidentTrackerEntries] = useState([]);
  const [aarData, setAarData] = useState({
    // I. Executive Summary
    executive_summary: {
      overall_closing: ''
    },
    // II. Incident Details and Objectives
    incident_details: {
      purpose_and_scope: ''
    },
    // III. Performance Analysis
    performance_analysis: {
      strengths: '',
      areas_for_improvement: '',
      communication_issues: '',
      resource_management: '',
      decision_making: '',
      plan_adherence: ''
    },
    // IV. Improvement Plan and Next Steps
    improvement_plan: {
      recommendations: '',
      responsible_parties: '',
      timelines: '',
      follow_up_plan: ''
    },
    // V. Incident Commander Final Comments
    commander_comments: ''
  });

  useEffect(() => {
    if (currentExercise?.id) {
      fetchAARData();
      fetchAgencyCoordinationEntries();
      fetchStatusSummaryEntries();
      fetchLessonsLearnedEntries();
      fetchHierarchicalObjectives();
      fetchActivityLogs();
      fetchParticipants();
      fetchResources();
      fetchCommsLogs();
      fetchCostUnitEntries();
      fetchScenarioEntries();
      fetchIncidentTrackerEntries();
    }
  }, [currentExercise?.id]);

  const fetchIncidentTrackerEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/ics-233?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setIncidentTrackerEntries(data);
      }
    } catch (error) {
      console.error('Error fetching incident tracker entries:', error);
    }
  };

  const fetchScenarioEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/scenario-entries?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setScenarioEntries(data);
      }
    } catch (error) {
      console.error('Error fetching scenario entries:', error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/activity-log/${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        // Sort chronologically by date/time (oldest first)
        const sorted = [...data].sort((a, b) => {
          const dateA = a.datePrepared || a.createdAt || '';
          const dateB = b.datePrepared || b.createdAt || '';
          const timeA = a.timePrepared || '';
          const timeB = b.timePrepared || '';
          const dtA = new Date(`${dateA} ${timeA}`) || 0;
          const dtB = new Date(`${dateB} ${timeB}`) || 0;
          return dtA - dtB;
        });
        setActivityLogs(sorted);
      } else {
        // Fallback to localStorage for backward compatibility
        const savedLogs = secureStorage.getPreference('ics214_activity_logs');
        if (savedLogs) {
          const allLogs = JSON.parse(savedLogs);
          const exerciseLogs = allLogs.filter(log => 
            log.exerciseId === currentExercise?.id || 
            log.exerciseName === currentExercise?.exercise_name ||
            log.exerciseName === currentExercise?.name
          );
          setActivityLogs(exerciseLogs);
        }
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  const fetchParticipants = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/exercises/${currentExercise.id}/participants`, { headers });
      if (response.ok) {
        const data = await response.json();
        // Sort alphabetically by name
        const sorted = [...data].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setParticipants(sorted);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const fetchResources = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/exercises/${currentExercise.id}/resources`, { headers });
      if (response.ok) {
        const data = await response.json();
        const sorted = [...data].sort((a, b) => (a.identification || '').localeCompare(b.identification || ''));
        setResources(sorted);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const fetchCommsLogs = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/ics-309?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCommsLogs(data);
      }
    } catch (error) {
      console.error('Error fetching comms logs:', error);
    }
  };

  const fetchCostUnitEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/cost-unit?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCostUnitEntries(data);
      }
    } catch (error) {
      console.error('Error fetching cost unit entries:', error);
    }
  };

  const fetchHierarchicalObjectives = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/hierarchical-objectives?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setHierarchicalObjectives(data);
      }
    } catch (error) {
      console.error('Error fetching hierarchical objectives:', error);
    }
  };

  const fetchLessonsLearnedEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/lessons-learned/exercise/${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setLessonsLearnedEntries(data);
      }
    } catch (error) {
      console.error('Error fetching lessons learned entries:', error);
    }
  };

  const fetchStatusSummaryEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/status-summary?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setStatusSummaryEntries(data);
      }
    } catch (error) {
      console.error('Error fetching status summary entries:', error);
    }
  };

  const fetchAgencyCoordinationEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/liaison-officer?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        // Filter for agency-coordination category entries
        const agencyEntries = data.filter(entry => entry.category === 'agency-coordination');
        setAgencyCoordinationEntries(agencyEntries);
      }
    } catch (error) {
      console.error('Error fetching agency coordination entries:', error);
    }
  };

  const fetchAARData = async () => {
    try {
      setLoading(true);
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/after-action-report/${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setAarData(prev => ({
            executive_summary: data.executive_summary || prev.executive_summary,
            incident_details: data.incident_details || prev.incident_details,
            performance_analysis: data.performance_analysis || prev.performance_analysis,
            improvement_plan: data.improvement_plan || prev.improvement_plan,
            commander_comments: data.commander_comments || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching AAR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAARData = async () => {
    try {
      setSaving(true);
      setSaveStatus(null);
      const token = secureStorage.getToken();
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      
      const response = await fetch(`${API_BASE_URL}/api/after-action-report/${currentExercise.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          exercise_id: currentExercise.id,
          ...aarData
        })
      });
      
      if (response.ok) {
        setSaveStatus('success');
        // Clear success status after 3 seconds
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving AAR:', error);
      setSaveStatus('error');
      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const clearAARData = async () => {
    if (!window.confirm('Are you sure you want to clear all After Action Report data? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/after-action-report/${currentExercise.id}`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        // Reset to empty state
        setAarData({
          executive_summary: {
            incident_overview: '',
            participating_organizations: '',
            key_findings: '',
            overall_closing: ''
          },
          incident_details: {
            purpose_and_scope: '',
            planned_objectives: '',
            methodology: '',
            actual_timeline: ''
          },
          performance_analysis: {
            strengths: '',
            areas_for_improvement: '',
            communication_issues: '',
            resource_management: '',
            decision_making: '',
            plan_adherence: ''
          },
          improvement_plan: {
            recommendations: '',
            responsible_parties: '',
            timelines: '',
            follow_up_plan: ''
          },
          commander_comments: ''
        });
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error clearing AAR:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const updateSection = (section, field, value) => {
    setAarData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const printReport = () => {
    const currentDateTime = new Date().toLocaleString();
    
    // Phase colors for print
    const phaseColors = {
      prevention: '#22c55e',
      mitigation: '#eab308',
      preparedness: '#f97316',
      response: '#ef4444',
      recovery: '#3b82f6'
    };
    
    const phaseLabels = {
      prevention: 'Prevention',
      mitigation: 'Mitigation',
      preparedness: 'Preparedness',
      response: 'Response',
      recovery: 'Recovery'
    };
    
    const printContent = `
      <html>
        <head>
          <title>After Action Report - ${currentExercise?.exercise_name || 'Exercise'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.5; color: #333; }
            .header { border-bottom: 3px solid #f97316; margin-bottom: 20px; padding-bottom: 10px; }
            .header-meta { font-size: 11px; color: #666; margin-bottom: 5px; }
            .header h1 { margin: 0 0 5px 0; color: #f97316; font-size: 24px; }
            .header h2 { margin: 0; color: #374151; font-size: 16px; font-weight: normal; }
            .section { margin-bottom: 20px; page-break-inside: avoid; }
            .section-number { color: #f97316; font-size: 14px; font-weight: bold; margin-bottom: 3px; }
            .section-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; color: #1f2937; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
            .subsection { margin-bottom: 10px; margin-left: 10px; }
            .subsection-title { font-size: 13px; font-weight: bold; margin-bottom: 5px; color: #4b5563; }
            .content { margin-bottom: 8px; color: #4b5563; white-space: pre-wrap; background: #f9f9f9; padding: 8px; border-radius: 4px; border-left: 4px solid #f97316; }
            .summary-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .summary-table th, .summary-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .summary-table th { background-color: #f97316; color: white; font-weight: bold; }
            .summary-table tr:nth-child(even) { background-color: #f9f9f9; }
            .field { margin: 5px 0; }
            .label { font-weight: bold; color: #666; }
            .value { margin-left: 8px; }
            .item { background: #f9f9f9; padding: 8px; margin: 8px 0; border-left: 4px solid #f97316; }
            .empty-note { color: #888; font-style: italic; }
            .phase-section { margin: 10px 0; padding: 10px; border-radius: 4px; }
            .phase-header { font-weight: bold; margin-bottom: 8px; }
            .scenario-entry { background: #fff; padding: 8px; margin: 5px 0; border-radius: 4px; border: 1px solid #ddd; }
            @media print { 
              @page { margin: 15mm; size: A4; }
              body { margin: 0; padding: 0; font-size: 11px; } 
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-meta">Generated: ${currentDateTime} | EXRSIM</div>
            <h1>After Action Report</h1>
            <h2>${currentExercise?.exercise_name || 'Exercise'}</h2>
            <table class="summary-table">
              <tr>
                <th>Exercise Type</th>
                <td>${currentExercise?.exercise_type || 'N/A'}</td>
                <th>Location</th>
                <td>${currentExercise?.location || 'N/A'}</td>
              </tr>
              <tr>
                <th>Start Date</th>
                <td>${currentExercise?.start_date || 'N/A'}</td>
                <th>End Date</th>
                <td>${currentExercise?.end_date || 'N/A'}</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <div class="section-number">SECTION I</div>
            <div class="section-title">Scenario</div>
            ${scenarioEntries.length === 0 ? 
              '<div class="content empty-note">No scenario entries recorded.</div>' :
              ['prevention', 'mitigation', 'preparedness', 'response', 'recovery'].map(phase => {
                const phaseEntries = scenarioEntries.filter(e => e.category === phase);
                return `
                  <div class="phase-section" style="border-left: 4px solid ${phaseColors[phase]}; background: ${phaseColors[phase]}10;">
                    <div class="phase-header" style="color: ${phaseColors[phase]};">${phaseLabels[phase]} Phase (${phaseEntries.length} ${phaseEntries.length === 1 ? 'entry' : 'entries'})</div>
                    ${phaseEntries.length === 0 ? 
                      '<div class="empty-note">No entries for this phase.</div>' :
                      phaseEntries.map(entry => `
                        <div class="scenario-entry">
                          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                            <strong>${entry.title || 'Untitled'}</strong>
                            <span style="font-size: 10px; color: #666;">
                              ${entry.priority ? `[${entry.priority}]` : ''} 
                              ${entry.status ? `- ${entry.status}` : ''}
                            </span>
                          </div>
                          ${entry.description ? `<div style="margin-bottom: 5px;">${entry.description}</div>` : ''}
                          <div style="font-size: 10px; color: #666;">
                            ${entry.timestamp ? `Time: ${new Date(entry.timestamp).toLocaleString()} | ` : ''}
                            ${entry.location ? `Location: ${entry.location} | ` : ''}
                            ${entry.contact_info ? `Contact: ${entry.contact_info}` : ''}
                          </div>
                          ${entry.notes ? `<div style="font-size: 10px; color: #888; margin-top: 5px;">Notes: ${entry.notes}</div>` : ''}
                        </div>
                      `).join('')
                    }
                  </div>
                `;
              }).join('')
            }
          </div>

          <div class="section">
            <div class="section-number">SECTION II</div>
            <div class="section-title">Executive Summary</div>
            <div class="subsection">
              <div class="subsection-title">Incident Overview</div>
              ${statusSummaryEntries.length > 0 ? 
                statusSummaryEntries.map((summary, index) => `
                  <div class="item">
                    <strong>Report #${summary.reportNumber || index + 1} - ${summary.reportVersion || 'Initial'}</strong>
                    <table class="summary-table" style="margin-top: 8px;">
                      ${summary.incidentCmdrName ? '<tr><th style="width:150px">Incident Commander</th><td>' + summary.incidentCmdrName + '</td></tr>' : ''}
                      ${summary.incidentMgmtOrg ? '<tr><th>Incident Mgmt Org</th><td>' + summary.incidentMgmtOrg + '</td></tr>' : ''}
                      ${summary.incidentDefinition ? '<tr><th>Incident Definition</th><td>' + summary.incidentDefinition + '</td></tr>' : ''}
                      ${summary.complexityLevel ? '<tr><th>Complexity Level</th><td>' + summary.complexityLevel + '</td></tr>' : ''}
                      ${summary.incidentAreaSize ? '<tr><th>Area Size</th><td>' + summary.incidentAreaSize + '</td></tr>' : ''}
                      ${summary.percentContained ? '<tr><th>Percent Contained</th><td>' + summary.percentContained + '%</td></tr>' : ''}
                      ${summary.startDate ? '<tr><th>Start Date/Time</th><td>' + summary.startDate + ' ' + (summary.startTime || '') + '</td></tr>' : ''}
                      ${summary.fromDate ? '<tr><th>Operational Period</th><td>' + summary.fromDate + ' ' + (summary.fromTime || '') + ' - ' + (summary.toDate || '') + ' ' + (summary.toTime || '') + '</td></tr>' : ''}
                    </table>
                  </div>
                `).join('') 
                : '<div class="content empty-note">No status summary entries recorded.</div>'
              }
            </div>
            <div class="subsection">
              <div class="subsection-title">Participating Organizations</div>
              ${agencyCoordinationEntries.length > 0 ? 
                agencyCoordinationEntries.map((entry, index) => `
                  <div class="item">
                    <strong>${entry.title || 'Agency Entry ' + (index + 1)}</strong>
                    ${entry.assisting_cooperating_agencies ? '<div class="field"><span class="label">Agencies:</span><span class="value">' + entry.assisting_cooperating_agencies + '</span></div>' : ''}
                    ${entry.agency_contact_info ? '<div class="field"><span class="label">Contact:</span><span class="value">' + entry.agency_contact_info + '</span></div>' : ''}
                    ${entry.interagency_coordination_status ? '<div class="field"><span class="label">Status:</span><span class="value">' + entry.interagency_coordination_status + '</span></div>' : ''}
                  </div>
                `).join('') 
                : '<div class="content empty-note">No agency coordination entries recorded.</div>'
              }
            </div>
          </div>

          <div class="section">
            <div class="section-number">SECTION III</div>
            <div class="section-title">Incident Details and Objectives</div>
            ${aarData.incident_details.purpose_and_scope ? `
            <div class="subsection">
              <div class="subsection-title">Purpose and Scope</div>
              <div class="content">${aarData.incident_details.purpose_and_scope}</div>
            </div>` : ''}
            <div class="subsection">
              <div class="subsection-title">Planned Objectives</div>
              <div class="content" style="padding: 6px;">
                ${hierarchicalObjectives.length === 0 ? '<span class="empty-note">No objectives found</span>' : hierarchicalObjectives.map((obj, objIdx) => `
                  <div style="margin-bottom: 6px; padding-left: 8px; border-left: 3px solid #f97316;">
                    <div><strong style="color: #f97316; font-size: 12px;">Objective ${objIdx + 1}: ${obj.title}</strong>${obj.priority ? ` <span style="font-size: 10px; color: ${obj.priority === 'high' ? '#ef4444' : obj.priority === 'medium' ? '#eab308' : '#22c55e'};">(${obj.priority})</span>` : ''}${obj.deadline ? ` <span style="font-size: 10px; color: #666;">| Due: ${new Date(obj.deadline).toLocaleDateString()}</span>` : ''}</div>
                    ${obj.description ? `<div style="font-size: 11px; color: #666; margin: 2px 0;">${obj.description}</div>` : ''}
                  </div>
                `).join('')}
              </div>
            </div>
            ${aarData.executive_summary.overall_closing ? `
            <div class="subsection">
              <div class="subsection-title">Overall Closing</div>
              <div class="content">${aarData.executive_summary.overall_closing}</div>
            </div>` : ''}
          </div>

          <div class="section">
            <div class="section-number">SECTION IV</div>
            <div class="section-title">Performance Analysis</div>
            ${lessonsLearnedEntries.filter(l => l.observation_status === 'worked_well').length > 0 ? `
            <div class="subsection">
              <div class="subsection-title">Strengths (Worked Well)</div>
              <div class="content">
                ${lessonsLearnedEntries.filter(l => l.observation_status === 'worked_well').map((lesson, index) => `
                  <div style="background: #f0fdf4; padding: 10px; margin-bottom: 8px; border-radius: 4px; border-left: 4px solid #22c55e;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                      <strong style="color: #166534;">${lesson.name || 'Lesson ' + (index + 1)}</strong>
                      <span style="color: #666; font-size: 11px;">${lesson.date || ''}</span>
                    </div>
                    <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
                      ${lesson.dotmplficc ? '<tr><td style="color: #666; width: 130px;">Category:</td><td>' + lesson.dotmplficc + '</td></tr>' : ''}
                      ${lesson.ics_organization ? '<tr><td style="color: #666;">ICS Organization:</td><td>' + lesson.ics_organization + '</td></tr>' : ''}
                      ${lesson.issues_observation ? '<tr><td style="color: #666; vertical-align: top;">Observation:</td><td>' + lesson.issues_observation + '</td></tr>' : ''}
                      ${lesson.recommendations ? '<tr><td style="color: #666; vertical-align: top;">Recommendations:</td><td>' + lesson.recommendations + '</td></tr>' : ''}
                      ${lesson.additional_comments ? '<tr><td style="color: #666; vertical-align: top;">Comments:</td><td>' + lesson.additional_comments + '</td></tr>' : ''}
                      ${lesson.recommended_actions ? '<tr><td style="color: #666; vertical-align: top;">Actions:</td><td>' + lesson.recommended_actions + '</td></tr>' : ''}
                    </table>
                  </div>
                `).join('')}
              </div>
            </div>` : ''}
            ${lessonsLearnedEntries.filter(l => l.observation_status === 'needs_improvement').length > 0 ? `
            <div class="subsection">
              <div class="subsection-title">Areas for Improvement</div>
              <div class="content">
                ${lessonsLearnedEntries.filter(l => l.observation_status === 'needs_improvement').map((lesson, index) => `
                  <div style="background: #fff7ed; padding: 10px; margin-bottom: 8px; border-radius: 4px; border-left: 4px solid #f97316;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                      <strong style="color: #c2410c;">${lesson.name || 'Lesson ' + (index + 1)}</strong>
                      <span style="color: #666; font-size: 11px;">${lesson.date || ''}</span>
                    </div>
                    <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
                      ${lesson.dotmplficc ? '<tr><td style="color: #666; width: 130px;">Category:</td><td>' + lesson.dotmplficc + '</td></tr>' : ''}
                      ${lesson.ics_organization ? '<tr><td style="color: #666;">ICS Organization:</td><td>' + lesson.ics_organization + '</td></tr>' : ''}
                      ${lesson.issues_observation ? '<tr><td style="color: #666; vertical-align: top;">Observation:</td><td>' + lesson.issues_observation + '</td></tr>' : ''}
                      ${lesson.recommendations ? '<tr><td style="color: #666; vertical-align: top;">Recommendations:</td><td>' + lesson.recommendations + '</td></tr>' : ''}
                      ${lesson.additional_comments ? '<tr><td style="color: #666; vertical-align: top;">Comments:</td><td>' + lesson.additional_comments + '</td></tr>' : ''}
                      ${lesson.recommended_actions ? '<tr><td style="color: #666; vertical-align: top;">Actions:</td><td>' + lesson.recommended_actions + '</td></tr>' : ''}
                    </table>
                  </div>
                `).join('')}
              </div>
            </div>` : ''}
            ${lessonsLearnedEntries.length === 0 ? '<div class="content empty-note">No lessons learned entries recorded.</div>' : ''}
          </div>

          <div class="section">
            <div class="section-number">SECTION V</div>
            <div class="section-title">Improvement Plan and Next Steps</div>
            ${aarData.improvement_plan.recommendations ? `
            <div class="subsection">
              <div class="subsection-title">Recommendations</div>
              <div class="content">${aarData.improvement_plan.recommendations}</div>
            </div>` : ''}
            ${aarData.improvement_plan.responsible_parties ? `
            <div class="subsection">
              <div class="subsection-title">Responsible Parties</div>
              <div class="content">${aarData.improvement_plan.responsible_parties}</div>
            </div>` : ''}
            ${aarData.improvement_plan.timelines ? `
            <div class="subsection">
              <div class="subsection-title">Timelines</div>
              <div class="content">${aarData.improvement_plan.timelines}</div>
            </div>` : ''}
            ${aarData.improvement_plan.follow_up_plan ? `
            <div class="subsection">
              <div class="subsection-title">Follow-Up Plan</div>
              <div class="content">${aarData.improvement_plan.follow_up_plan}</div>
            </div>` : ''}
          </div>

          ${aarData.commander_comments ? `
          <div class="section">
            <div class="section-number">SECTION VI</div>
            <div class="section-title">Incident Commander Final Comments</div>
            <div class="subsection">
              <div class="subsection-title">Commander's Comments</div>
              <div class="content">${aarData.commander_comments}</div>
            </div>
          </div>` : ''}

          ${(() => {
            const allIncidentItems = incidentTrackerEntries.flatMap(e => (e.action_items || []).map(item => ({ ...item, _exerciseName: e.exercise_name })));
            if (allIncidentItems.length === 0) return '';
            const statusColor = (s) => {
              const v = (s || '').toLowerCase();
              if (v.includes('complete') || v.includes('closed')) return '#16a34a';
              if (v.includes('progress') || v.includes('open')) return '#f59e0b';
              if (v.includes('overdue') || v.includes('block')) return '#dc2626';
              return '#6b7280';
            };
            return `
          <div class="section" style="page-break-before: always;">
            <div class="section-number" style="background: #dc2626;">ANNEX A</div>
            <div class="section-title">Incident Tracker (ICS 233)</div>
            <div class="subsection">
              <div class="subsection-title">Action Items & Tracking (${allIncidentItems.length} total)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
                  <thead>
                    <tr>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left; width: 30px;">#</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Item / Action</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">For POC</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: center; width: 60px;">Briefed</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left; width: 90px;">Start Date</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left; width: 90px;">Target Date</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left; width: 90px;">Actual Date</th>
                      <th style="background: #dc2626; color: white; border: 1px solid #ddd; padding: 4px; text-align: left; width: 100px;">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${allIncidentItems.map((item, idx) => `
                      <tr style="background: ${idx % 2 === 0 ? '#fff' : '#f9fafb'};">
                        <td style="border: 1px solid #ddd; padding: 3px;">${item.no || (idx + 1)}</td>
                        <td style="border: 1px solid #ddd; padding: 3px; font-weight: 600;">${item.item || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${item.for_poc || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px; text-align: center;">${item.briefed_poc ? '✓' : '—'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${item.start_date || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${item.target_date || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${item.actual_date || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px; color: ${statusColor(item.status)}; font-weight: 600;">${item.status || '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          </div>`;
          })()}

          ${participants.length > 0 ? `
          <div class="section" style="page-break-before: always;">
            <div class="section-number" style="background: #3b82f6;">ANNEX B</div>
            <div class="section-title">Exercise Participants</div>
            <div class="subsection">
              <div class="subsection-title">Personnel Assigned to Exercise (${participants.length} total)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
                  <thead>
                    <tr>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">#</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Name</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Organization</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Role</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Position</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Strike Team</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Task Force</th>
                      <th style="background: #3b82f6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${participants.map((p, idx) => `
                      <tr style="background: ${idx % 2 === 0 ? '#fff' : '#f9fafb'};">
                        <td style="border: 1px solid #ddd; padding: 3px;">${idx + 1}</td>
                        <td style="border: 1px solid #ddd; padding: 3px; font-weight: 600;">${p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${p.organization || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${(p.role || 'N/A').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${p.position || p.incidentAssignment || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${p.strikeTeam || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${p.taskForce || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${[p.email, p.cellPhone || p.phone].filter(Boolean).join(', ') || 'N/A'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          </div>` : ''}

          ${resources.length > 0 ? `
          <div class="section" style="page-break-before: always;">
            <div class="section-number" style="background: #14b8a6;">ANNEX C</div>
            <div class="section-title">Exercise Resources</div>
            <div class="subsection">
              <div class="subsection-title">Resources from Res. Management (${resources.length} total)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
                  <thead>
                    <tr>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">#</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Identification</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Type</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Status</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Location</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Strike Team</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Task Force</th>
                      <th style="background: #14b8a6; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${resources.map((r, idx) => `
                      <tr style="background: ${idx % 2 === 0 ? '#fff' : '#f9fafb'};">
                        <td style="border: 1px solid #ddd; padding: 3px;">${idx + 1}</td>
                        <td style="border: 1px solid #ddd; padding: 3px; font-weight: 600;">${r.identification || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${r.resource_type || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${r.status || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${r.location || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${r.strike_team || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${r.task_force || '-'}</td>
                        <td style="border: 1px solid #ddd; padding: 3px;">${[r.contact_person, r.contact_phone].filter(Boolean).join(', ') || 'N/A'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          </div>` : ''}

          ${costUnitEntries.length > 0 ? (() => {
            const parseCost = (val) => { const n = parseFloat(String(val || '0').replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };
            const allPersonnel = costUnitEntries.flatMap(e => e.personnel_costs || []);
            const allEquipment = costUnitEntries.flatMap(e => e.equipment_costs || []);
            const allProcurement = costUnitEntries.flatMap(e => e.procurement_contracts || []);
            const allIncident = costUnitEntries.flatMap(e => e.incident_costs || []);
            const totalPersonnel = allPersonnel.reduce((s, i) => s + parseCost(i.total_cost), 0);
            const totalEquipment = allEquipment.reduce((s, i) => s + parseCost(i.total_cost), 0);
            const totalProcurement = allProcurement.reduce((s, i) => s + parseCost(i.contract_value), 0);
            const totalIncident = allIncident.reduce((s, i) => s + parseCost(i.amount), 0);
            const grandTotal = totalPersonnel + totalEquipment + totalProcurement + totalIncident;
            const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const thStyle = 'background: #16a34a; color: white; border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 10px;';
            const tdStyle = 'border: 1px solid #ddd; padding: 3px; font-size: 10px;';
            return `
          <div class="section" style="page-break-before: always;">
            <div class="section-number" style="background: #16a34a;">ANNEX D</div>
            <div class="section-title">Financials (Cost Unit)</div>

            <div class="subsection" style="margin-bottom: 12px;">
              <div class="subsection-title">Personnel Costs (${allPersonnel.length} entries)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse;">
                  <thead><tr>
                    <th style="${thStyle}">#</th><th style="${thStyle}">Name</th><th style="${thStyle}">Position</th><th style="${thStyle}">Agency</th><th style="${thStyle}">Hours</th><th style="${thStyle}">Rate</th><th style="${thStyle}">Total</th>
                  </tr></thead>
                  <tbody>
                    ${allPersonnel.length === 0 ? '<tr><td colspan="7" style="'+tdStyle+' text-align:center; color:#999;">No entries</td></tr>' : allPersonnel.map((p, i) => '<tr style="background:'+(i%2===0?'#fff':'#f9fafb')+'"><td style="'+tdStyle+'">'+(i+1)+'</td><td style="'+tdStyle+'">'+(p.name||'N/A')+'</td><td style="'+tdStyle+'">'+(p.position||'-')+'</td><td style="'+tdStyle+'">'+(p.agency||'-')+'</td><td style="'+tdStyle+'">'+(p.hours_worked||'-')+'</td><td style="'+tdStyle+'">'+(p.hourly_rate||'-')+'</td><td style="'+tdStyle+' font-weight:600;">'+(p.total_cost||'$0.00')+'</td></tr>').join('')}
                    <tr style="background:#f0fdf4;"><td colspan="6" style="${tdStyle} text-align:right; font-weight:700;">Subtotal Personnel:</td><td style="${tdStyle} font-weight:700;">${fmt(totalPersonnel)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="subsection" style="margin-bottom: 12px;">
              <div class="subsection-title">Equipment Costs (${allEquipment.length} entries)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse;">
                  <thead><tr>
                    <th style="${thStyle}">#</th><th style="${thStyle}">Equipment</th><th style="${thStyle}">Type</th><th style="${thStyle}">Qty</th><th style="${thStyle}">Daily Rate</th><th style="${thStyle}">Days</th><th style="${thStyle}">Total</th>
                  </tr></thead>
                  <tbody>
                    ${allEquipment.length === 0 ? '<tr><td colspan="7" style="'+tdStyle+' text-align:center; color:#999;">No entries</td></tr>' : allEquipment.map((e, i) => '<tr style="background:'+(i%2===0?'#fff':'#f9fafb')+'"><td style="'+tdStyle+'">'+(i+1)+'</td><td style="'+tdStyle+'">'+(e.equipment_name||'N/A')+'</td><td style="'+tdStyle+'">'+(e.equipment_type||'-')+'</td><td style="'+tdStyle+'">'+(e.quantity||'-')+'</td><td style="'+tdStyle+'">'+(e.daily_rate||'-')+'</td><td style="'+tdStyle+'">'+(e.days_used||'-')+'</td><td style="'+tdStyle+' font-weight:600;">'+(e.total_cost||'$0.00')+'</td></tr>').join('')}
                    <tr style="background:#f0fdf4;"><td colspan="6" style="${tdStyle} text-align:right; font-weight:700;">Subtotal Equipment:</td><td style="${tdStyle} font-weight:700;">${fmt(totalEquipment)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="subsection" style="margin-bottom: 12px;">
              <div class="subsection-title">Procurement Contracts (${allProcurement.length} entries)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse;">
                  <thead><tr>
                    <th style="${thStyle}">#</th><th style="${thStyle}">Contract #</th><th style="${thStyle}">Vendor</th><th style="${thStyle}">Description</th><th style="${thStyle}">Date</th><th style="${thStyle}">Status</th><th style="${thStyle}">Value</th>
                  </tr></thead>
                  <tbody>
                    ${allProcurement.length === 0 ? '<tr><td colspan="7" style="'+tdStyle+' text-align:center; color:#999;">No entries</td></tr>' : allProcurement.map((c, i) => '<tr style="background:'+(i%2===0?'#fff':'#f9fafb')+'"><td style="'+tdStyle+'">'+(i+1)+'</td><td style="'+tdStyle+'">'+(c.contract_number||'N/A')+'</td><td style="'+tdStyle+'">'+(c.vendor_name||'-')+'</td><td style="'+tdStyle+'">'+(c.description||'-')+'</td><td style="'+tdStyle+'">'+(c.contract_date||'-')+'</td><td style="'+tdStyle+'">'+(c.status||'-')+'</td><td style="'+tdStyle+' font-weight:600;">'+(c.contract_value||'$0.00')+'</td></tr>').join('')}
                    <tr style="background:#f0fdf4;"><td colspan="6" style="${tdStyle} text-align:right; font-weight:700;">Subtotal Procurement:</td><td style="${tdStyle} font-weight:700;">${fmt(totalProcurement)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="subsection" style="margin-bottom: 12px;">
              <div class="subsection-title">Incident-Related Costs (${allIncident.length} entries)</div>
              <div class="content" style="padding: 4px;">
                <table style="width: 100%; border-collapse: collapse;">
                  <thead><tr>
                    <th style="${thStyle}">#</th><th style="${thStyle}">Date</th><th style="${thStyle}">Category</th><th style="${thStyle}">Description</th><th style="${thStyle}">Approved By</th><th style="${thStyle}">Notes</th><th style="${thStyle}">Amount</th>
                  </tr></thead>
                  <tbody>
                    ${allIncident.length === 0 ? '<tr><td colspan="7" style="'+tdStyle+' text-align:center; color:#999;">No entries</td></tr>' : allIncident.map((c, i) => '<tr style="background:'+(i%2===0?'#fff':'#f9fafb')+'"><td style="'+tdStyle+'">'+(i+1)+'</td><td style="'+tdStyle+'">'+(c.date||'-')+'</td><td style="'+tdStyle+'">'+(c.category||'-')+'</td><td style="'+tdStyle+'">'+(c.description||'N/A')+'</td><td style="'+tdStyle+'">'+(c.approved_by||'-')+'</td><td style="'+tdStyle+'">'+(c.notes||'-')+'</td><td style="'+tdStyle+' font-weight:600;">'+(c.amount||'$0.00')+'</td></tr>').join('')}
                    <tr style="background:#f0fdf4;"><td colspan="6" style="${tdStyle} text-align:right; font-weight:700;">Subtotal Incident:</td><td style="${tdStyle} font-weight:700;">${fmt(totalIncident)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style="background: #16a34a; color: white; padding: 12px 16px; border-radius: 6px; text-align: right; font-size: 16px; font-weight: 700;">
              Grand Total: ${fmt(grandTotal)}
            </div>
          </div>`;
          })() : ''}

          ${activityLogs.length > 0 ? `
          <div class="section" style="page-break-before: always;">
            <div class="section-number" style="background: #f97316;">ANNEX E</div>
            <div class="section-title">Activity Log Timeline</div>
            <div class="subsection">
              <div class="subsection-title">Chronological Activity Record (ICS 214)</div>
              <div class="content" style="padding: 6px;">
                ${[...activityLogs].sort((a, b) => {
                  const dateA = a.datePrepared || a.createdAt || '';
                  const dateB = b.datePrepared || b.createdAt || '';
                  const timeA = a.timePrepared || '';
                  const timeB = b.timePrepared || '';
                  return new Date(`${dateA} ${timeA}`) - new Date(`${dateB} ${timeB}`);
                }).map((log, logIdx) => `
                  <div style="margin-bottom: 10px; padding: 8px; border-left: 3px solid #f97316; background: #fafafa;">
                    <div style="display: flex; justify-content: space-between;"><strong style="color: #f97316; font-size: 11px;">Activity Log ${logIdx + 1}</strong><span style="color: #666; font-size: 10px;">${log.datePrepared || ''} ${log.timePrepared || ''}</span></div>
                    <div style="font-size: 11px;"><span class="label">Prepared by:</span> ${log.name || 'N/A'} | <span class="label">Position:</span> ${log.icsPosition || 'N/A'}</div>
                    <div style="font-size: 11px;"><span class="label">Period:</span> ${log.operatingPeriodFrom ? new Date(log.operatingPeriodFrom).toLocaleString() : 'N/A'} - ${log.operatingPeriodTo ? new Date(log.operatingPeriodTo).toLocaleString() : 'N/A'}</div>
                    ${log.majorEvents && log.majorEvents.length > 0 ? `
                      <table style="width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 10px;">
                        <thead><tr><th style="background: #f97316; color: white; border: 1px solid #ddd; padding: 3px; text-align: left; width: 140px;">Date/Time</th><th style="background: #f97316; color: white; border: 1px solid #ddd; padding: 3px; text-align: left;">Event</th></tr></thead>
                        <tbody>${[...log.majorEvents].sort((a, b) => (a.dateTime ? new Date(a.dateTime) : 0) - (b.dateTime ? new Date(b.dateTime) : 0)).map(event => `<tr><td style="border: 1px solid #ddd; padding: 3px;">${event.dateTime ? new Date(event.dateTime).toLocaleString() : 'N/A'}</td><td style="border: 1px solid #ddd; padding: 3px;">${event.event || 'No description'}</td></tr>`).join('')}</tbody>
                      </table>
                    ` : ''}
                  </div>
                `).join('')}
              </div>
            </div>
          </div>` : ''}

          ${commsLogs.length > 0 ? `
          <div class="section" style="page-break-before: always;">
            <div class="section-number" style="background: #a855f7;">ANNEX F</div>
            <div class="section-title">Communications Log (ICS 309)</div>
            ${commsLogs.map((entry, entryIdx) => {
              const msgs = entry.message_log || [];
              return `
              <div class="subsection" style="margin-bottom: 16px;">
                <div class="subsection-title">${entry.radio_network_name || 'Entry ' + (entryIdx + 1)} — Operator: ${entry.radio_operator || 'N/A'} | Period: ${entry.operational_period_from ? entry.operational_period_from.replace('T', ' ') : 'N/A'} → ${entry.operational_period_to ? entry.operational_period_to.replace('T', ' ') : 'N/A'}</div>
                <div class="content" style="padding: 4px;">
                  <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
                    <thead>
                      <tr>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">#</th>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Date / Time</th>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">From</th>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">From Msg #</th>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">To</th>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">To Msg #</th>
                        <th style="background: #a855f7; color: white; border: 1px solid #ddd; padding: 4px; text-align: left;">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${msgs.map((m, mIdx) => `
                        <tr style="background: ${mIdx % 2 === 0 ? '#fff' : '#f9fafb'};">
                          <td style="border: 1px solid #ddd; padding: 3px;">${mIdx + 1}</td>
                          <td style="border: 1px solid #ddd; padding: 3px;">${m.time ? m.time.replace('T', ' ') : 'N/A'}</td>
                          <td style="border: 1px solid #ddd; padding: 3px; font-weight: 600;">${m.from_callsign || 'N/A'}</td>
                          <td style="border: 1px solid #ddd; padding: 3px;">${m.from_msg_number || '-'}</td>
                          <td style="border: 1px solid #ddd; padding: 3px; font-weight: 600;">${m.to_callsign || 'N/A'}</td>
                          <td style="border: 1px solid #ddd; padding: 3px;">${m.to_msg_number || '-'}</td>
                          <td style="border: 1px solid #ddd; padding: 3px;">${m.message || 'N/A'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
              </div>`;
            }).join('')}
          </div>` : ''}

        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const inputClass = `w-full p-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none`;
  const textareaClass = `${inputClass} min-h-[100px] resize-y`;
  const labelClass = `block text-sm font-medium text-gray-300 mb-2`;
  const sectionHeaderClass = `text-lg font-bold text-blue-400 mb-1`;
  const sectionDescClass = `text-sm text-gray-400 mb-4`;

  // Emergency phases configuration for scenario display
  const emergencyPhases = [
    { key: 'prevention', label: 'Prevention', color: 'text-green-500', bgColor: 'bg-green-500/10', borderColor: 'border-green-500', Icon: Shield },
    { key: 'mitigation', label: 'Mitigation', color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500', Icon: Settings },
    { key: 'preparedness', label: 'Preparedness', color: 'text-orange-500', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500', Icon: ClipboardList },
    { key: 'response', label: 'Response', color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500', Icon: AlertTriangle },
    { key: 'recovery', label: 'Recovery', color: 'text-blue-500', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500', Icon: RefreshCw }
  ];

  // Group scenario entries by phase
  const scenarioByPhase = emergencyPhases.map(phase => ({
    ...phase,
    entries: scenarioEntries.filter(entry => entry.category === phase.key)
  }));

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">After Action Report</h1>
          <p className="text-gray-400">
            {currentExercise?.exercise_name || 'Exercise'} - Comprehensive Review
          </p>
        </div>
        <div className="flex space-x-3">
          <Button 
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            onClick={printReport}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Report
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={saveAARData}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Report
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Section I: Scenario */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">I</div>
            <div>
              <CardTitle className="text-blue-400">Scenario</CardTitle>
              <CardDescription className="text-gray-400">
                Exercise scenario information organized by emergency management phases
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          {scenarioEntries.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No scenario entries found for this exercise.</p>
              <p className="text-sm text-gray-500 mt-1">Add scenario information in the Incident Action Plan → Scenario menu.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {scenarioByPhase.map(phase => {
                const PhaseIcon = phase.Icon;
                return (
                  <div key={phase.key} className={`rounded-lg border ${phase.borderColor} ${phase.bgColor} p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                      <PhaseIcon className={`h-5 w-5 ${phase.color}`} />
                      <h3 className={`font-semibold ${phase.color}`}>{phase.label} Phase</h3>
                      <span className="text-gray-400 text-sm">({phase.entries.length} {phase.entries.length === 1 ? 'entry' : 'entries'})</span>
                    </div>
                    {phase.entries.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">No entries for this phase.</p>
                    ) : (
                      <div className="space-y-3">
                        {phase.entries.map((entry, idx) => (
                          <div key={entry.id || idx} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-white font-medium">{entry.title || 'Untitled Entry'}</h4>
                              <div className="flex items-center gap-2">
                                {entry.priority && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    entry.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                                    entry.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-green-500/20 text-green-400'
                                  }`}>
                                    {entry.priority}
                                  </span>
                                )}
                                {entry.status && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    entry.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                                    entry.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {entry.status}
                                  </span>
                                )}
                              </div>
                            </div>
                            {entry.description && (
                              <p className="text-gray-300 text-sm mb-2">{entry.description}</p>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              {entry.timestamp && (
                                <div>
                                  <span className="text-gray-500">Time: </span>
                                  <span className="text-gray-300">{new Date(entry.timestamp).toLocaleString()}</span>
                                </div>
                              )}
                              {entry.location && (
                                <div>
                                  <span className="text-gray-500">Location: </span>
                                  <span className="text-gray-300">{entry.location}</span>
                                </div>
                              )}
                              {entry.contact_info && (
                                <div>
                                  <span className="text-gray-500">Contact: </span>
                                  <span className="text-gray-300">{entry.contact_info}</span>
                                </div>
                              )}
                            </div>
                            {entry.notes && (
                              <div className="mt-2 text-xs">
                                <span className="text-gray-500">Notes: </span>
                                <span className="text-gray-400">{entry.notes}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section II: Executive Summary */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">II</div>
            <div>
              <CardTitle className="text-blue-400">Executive Summary</CardTitle>
              <CardDescription className="text-gray-400">
                A high-level overview for leadership
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          {/* Incident Overview - from Status Summary Form */}
          <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Incident Overview
              <span className="text-gray-500 font-normal ml-2">From Incident Status - Status Summary Form</span>
            </label>
            {statusSummaryEntries.length > 0 ? (
              <div className="space-y-4 mt-3">
                {statusSummaryEntries.map((summary, index) => (
                  <div key={summary.id || index} className="bg-gray-700 p-4 rounded border border-gray-600">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-white font-medium">
                        Report #{summary.reportNumber || index + 1} - {summary.reportVersion || 'Initial'}
                      </h4>
                      <span className="text-xs text-gray-400">
                        {summary.created_at ? new Date(summary.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {summary.incidentCmdrName && (
                        <div>
                          <p className="text-xs text-gray-400">Incident Commander</p>
                          <p className="text-gray-300">{summary.incidentCmdrName}</p>
                        </div>
                      )}
                      {summary.incidentMgmtOrg && (
                        <div>
                          <p className="text-xs text-gray-400">Incident Mgmt Organization</p>
                          <p className="text-gray-300">{summary.incidentMgmtOrg}</p>
                        </div>
                      )}
                      {summary.incidentDefinition && (
                        <div className="md:col-span-2">
                          <p className="text-xs text-gray-400">Incident Definition</p>
                          <p className="text-gray-300">{summary.incidentDefinition}</p>
                        </div>
                      )}
                      {summary.complexityLevel && (
                        <div>
                          <p className="text-xs text-gray-400">Complexity Level</p>
                          <p className="text-gray-300">{summary.complexityLevel}</p>
                        </div>
                      )}
                      {summary.incidentAreaSize && (
                        <div>
                          <p className="text-xs text-gray-400">Incident Area Size</p>
                          <p className="text-gray-300">{summary.incidentAreaSize}</p>
                        </div>
                      )}
                      {summary.percentContained && (
                        <div>
                          <p className="text-xs text-gray-400">Percent Contained</p>
                          <p className="text-gray-300">{summary.percentContained}%</p>
                        </div>
                      )}
                      {summary.completed && (
                        <div>
                          <p className="text-xs text-gray-400">Completed</p>
                          <p className="text-gray-300">{summary.completed}</p>
                        </div>
                      )}
                      {(summary.startDate || summary.startTime) && (
                        <div>
                          <p className="text-xs text-gray-400">Start Date/Time</p>
                          <p className="text-gray-300">{summary.startDate} {summary.startTime}</p>
                        </div>
                      )}
                      {(summary.fromDate || summary.toDate) && (
                        <div>
                          <p className="text-xs text-gray-400">Operational Period</p>
                          <p className="text-gray-300">
                            {summary.fromDate} {summary.fromTime} - {summary.toDate} {summary.toTime}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-700 p-4 rounded border border-gray-600 mt-3">
                <p className="text-gray-400 text-center">
                  No status summary entries found. Add entries in ICS → Incident Status → Status Summary.
                </p>
              </div>
            )}
          </div>
          
          {/* Participating Organizations - from Liaison Officer Agency Coordination */}
          <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Participating Organizations
              <span className="text-gray-500 font-normal ml-2">Agencies from Liaison Officer - Agency Coordination Entries</span>
            </label>
            {agencyCoordinationEntries.length > 0 ? (
              <div className="space-y-3 mt-3">
                {agencyCoordinationEntries.map((entry, index) => (
                  <div key={entry.id || index} className="bg-gray-700 p-3 rounded border border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{entry.title || `Agency Entry ${index + 1}`}</h4>
                      <span className="text-xs text-gray-400">
                        {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    {entry.assisting_cooperating_agencies && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-400">Assisting/Cooperating Agencies:</p>
                        <p className="text-gray-300 text-sm">{entry.assisting_cooperating_agencies}</p>
                      </div>
                    )}
                    {entry.agency_contact_info && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-400">Contact Info:</p>
                        <p className="text-gray-300 text-sm">{entry.agency_contact_info}</p>
                      </div>
                    )}
                    {entry.interagency_coordination_status && (
                      <div>
                        <p className="text-xs text-gray-400">Coordination Status:</p>
                        <p className="text-gray-300 text-sm">{entry.interagency_coordination_status}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-700 p-4 rounded border border-gray-600 mt-3">
                <p className="text-gray-400 text-center">
                  No agency coordination entries found. Add entries in the Liaison Officer → Agency Coordination section.
                </p>
              </div>
            )}
          </div>
          
        </CardContent>
      </Card>

      {/* Section III: Incident Details and Objectives */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">III</div>
            <div>
              <CardTitle className="text-blue-400">Incident Details and Objectives</CardTitle>
              <CardDescription className="text-gray-400">
                Context and baseline for evaluation
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Purpose and Scope
              <span className="text-gray-500 font-normal ml-2">Specific goals of the AAR ( Communication and Information management, Resource management (personnel, equipment, funding), Decision-making processes and Adherence to existing plans and procedures)</span>
            </label>
            <textarea
              className={textareaClass}
              placeholder="Define the purpose and scope..."
              value={aarData.incident_details.purpose_and_scope}
              onChange={(e) => updateSection('incident_details', 'purpose_and_scope', e.target.value)}
            />
          </div>
          
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Planned Objectives
              <span className="text-gray-500 font-normal ml-2">Hierarchical view of objectives, strategies, and tactics from the Objectives module</span>
            </label>
            <div className="mt-2 bg-gray-900 border border-gray-600 rounded-lg p-4 max-h-[400px] overflow-y-auto">
              {hierarchicalObjectives.length === 0 ? (
                <p className="text-gray-500 italic">No objectives found. Add objectives in the Objectives menu to see them here.</p>
              ) : (
                <div className="space-y-4">
                  {hierarchicalObjectives.map((objective, objIndex) => (
                    <div key={objective.id} className="border-l-4 border-blue-500 pl-4">
                      <div className="flex items-start gap-2">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">OBJ {objIndex + 1}</span>
                        <div className="flex-1">
                          <h4 className="text-blue-300 font-semibold">{objective.title}</h4>
                          {objective.description && (
                            <p className="text-gray-400 text-sm mt-1">{objective.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {objective.priority && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                objective.priority === 'high' ? 'bg-red-900 text-red-300' :
                                objective.priority === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                                'bg-green-900 text-green-300'
                              }`}>
                                {objective.priority} priority
                              </span>
                            )}
                            {objective.status && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                objective.status === 'completed' ? 'bg-green-900 text-green-300' :
                                objective.status === 'in-progress' ? 'bg-blue-900 text-blue-300' :
                                'bg-gray-700 text-gray-300'
                              }`}>
                                {objective.status}
                              </span>
                            )}
                            {objective.deadline && (
                              <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Deadline: {new Date(objective.deadline).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Strategies */}
                      {objective.strategies && objective.strategies.length > 0 && (
                        <div className="mt-3 ml-4 space-y-3">
                          {objective.strategies.map((strategy, stratIndex) => (
                            <div key={strategy.id} className="border-l-2 border-purple-500 pl-3">
                              <div className="flex items-start gap-2">
                                <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded">S{stratIndex + 1}</span>
                                <div className="flex-1">
                                  <h5 className="text-purple-300 font-medium text-sm">{strategy.title}</h5>
                                  {strategy.description && (
                                    <p className="text-gray-500 text-xs mt-0.5">{strategy.description}</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Tactics */}
                              {strategy.tactics && strategy.tactics.length > 0 && (
                                <div className="mt-2 ml-4 space-y-1">
                                  {strategy.tactics.map((tactic, tacIndex) => (
                                    <div key={tactic.id} className="flex items-start gap-2 border-l border-orange-500 pl-2">
                                      <span className="bg-orange-600 text-white text-xs font-bold px-1.5 py-0.5 rounded text-[10px]">T{tacIndex + 1}</span>
                                      <div className="flex-1">
                                        <p className="text-orange-300 text-xs">{tactic.action || tactic.title}</p>
                                        {tactic.assigned_to && (
                                          <p className="text-gray-500 text-[10px]">Assigned: {tactic.assigned_to}</p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Overall Closing
              <span className="text-gray-500 font-normal ml-2">General statement about whether objectives were met and value of using results to update plans</span>
            </label>
            <textarea
              className={textareaClass}
              placeholder="Provide overall closing statement..."
              value={aarData.executive_summary.overall_closing}
              onChange={(e) => updateSection('executive_summary', 'overall_closing', e.target.value)}
            />
          </div>
          
        </CardContent>
      </Card>

      {/* Section IV: Performance Analysis */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">IV</div>
            <div>
              <CardTitle className="text-blue-400">Performance Analysis</CardTitle>
              <CardDescription className="text-gray-400">
                Strengths and Areas for Improvement - this section will download any Lessons Learned that have been created in the Exercise Improvement menu
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          <div>
            <label className={labelClass}>
              <span className="text-green-400">●</span> Strengths
              <span className="text-gray-500 font-normal ml-2">Lessons Learned entries marked as "Worked Well"</span>
            </label>
            <div className="mt-2 bg-gray-900 border border-gray-600 rounded-lg p-4 max-h-[500px] overflow-y-auto">
              {lessonsLearnedEntries.filter(l => l.observation_status === 'worked_well').length === 0 ? (
                <p className="text-gray-500 italic">No lessons learned marked as "Worked Well". Update entries in the Lessons Learned menu to see them here.</p>
              ) : (
                <div className="space-y-4">
                  {lessonsLearnedEntries.filter(l => l.observation_status === 'worked_well').map((lesson, index) => (
                    <div key={lesson.id || index} className="border-l-4 border-green-500 pl-4 pb-4 border-b border-gray-700 last:border-b-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-green-300 font-semibold">{lesson.name || `Lesson ${index + 1}`}</h4>
                        <span className="text-gray-400 text-sm">{lesson.date || ''}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                        <div>
                          <span className="text-gray-500">Category:</span>
                          <span className="text-white ml-2">{lesson.dotmplficc || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">ICS Organization:</span>
                          <span className="text-white ml-2">{lesson.ics_organization || 'N/A'}</span>
                        </div>
                      </div>
                      {lesson.issues_observation && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Issues / Observation:</p>
                          <p className="text-gray-300 text-sm">{lesson.issues_observation}</p>
                        </div>
                      )}
                      {lesson.recommendations && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Recommendations:</p>
                          <p className="text-gray-300 text-sm">{lesson.recommendations}</p>
                        </div>
                      )}
                      {lesson.additional_comments && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Additional Comments:</p>
                          <p className="text-gray-300 text-sm">{lesson.additional_comments}</p>
                        </div>
                      )}
                      {lesson.recommended_actions && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Recommended Actions:</p>
                          <p className="text-gray-300 text-sm">{lesson.recommended_actions}</p>
                        </div>
                      )}
                      {lesson.final_authority_remarks && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Final Authority Remarks:</p>
                          <p className="text-gray-300 text-sm">{lesson.final_authority_remarks}</p>
                        </div>
                      )}
                      {(lesson.testing_done_by || lesson.procedures_written_by || lesson.implement_new_ll_by) && (
                        <div className="grid grid-cols-3 gap-2 mt-2 text-sm bg-gray-800 p-2 rounded">
                          {lesson.testing_done_by && (
                            <div>
                              <p className="text-gray-500 text-xs">Testing Done By:</p>
                              <p className="text-gray-300 text-xs">{lesson.testing_done_by}</p>
                            </div>
                          )}
                          {lesson.procedures_written_by && (
                            <div>
                              <p className="text-gray-500 text-xs">Procedures Written By:</p>
                              <p className="text-gray-300 text-xs">{lesson.procedures_written_by}</p>
                            </div>
                          )}
                          {lesson.implement_new_ll_by && (
                            <div>
                              <p className="text-gray-500 text-xs">Implemented New LL By:</p>
                              <p className="text-gray-300 text-xs">{lesson.implement_new_ll_by}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className={labelClass}>
              <span className="text-orange-400">●</span> Areas for Improvement / Challenges
              <span className="text-gray-500 font-normal ml-2">Lessons Learned entries marked as "Needs Improvement"</span>
            </label>
            <div className="mt-2 bg-gray-900 border border-gray-600 rounded-lg p-4 max-h-[500px] overflow-y-auto">
              {lessonsLearnedEntries.filter(l => l.observation_status === 'needs_improvement').length === 0 ? (
                <p className="text-gray-500 italic">No lessons learned marked as "Needs Improvement". Update entries in the Lessons Learned menu to see them here.</p>
              ) : (
                <div className="space-y-4">
                  {lessonsLearnedEntries.filter(l => l.observation_status === 'needs_improvement').map((lesson, index) => (
                    <div key={lesson.id || index} className="border-l-4 border-orange-500 pl-4 pb-4 border-b border-gray-700 last:border-b-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-orange-300 font-semibold">{lesson.name || `Lesson ${index + 1}`}</h4>
                        <span className="text-gray-400 text-sm">{lesson.date || ''}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                        <div>
                          <span className="text-gray-500">Category:</span>
                          <span className="text-white ml-2">{lesson.dotmplficc || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">ICS Organization:</span>
                          <span className="text-white ml-2">{lesson.ics_organization || 'N/A'}</span>
                        </div>
                      </div>
                      {lesson.issues_observation && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Issues / Observation:</p>
                          <p className="text-gray-300 text-sm">{lesson.issues_observation}</p>
                        </div>
                      )}
                      {lesson.recommendations && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Recommendations:</p>
                          <p className="text-gray-300 text-sm">{lesson.recommendations}</p>
                        </div>
                      )}
                      {lesson.additional_comments && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Additional Comments:</p>
                          <p className="text-gray-300 text-sm">{lesson.additional_comments}</p>
                        </div>
                      )}
                      {lesson.recommended_actions && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Recommended Actions:</p>
                          <p className="text-gray-300 text-sm">{lesson.recommended_actions}</p>
                        </div>
                      )}
                      {lesson.final_authority_remarks && (
                        <div className="mb-2">
                          <p className="text-gray-500 text-xs">Final Authority Remarks:</p>
                          <p className="text-gray-300 text-sm">{lesson.final_authority_remarks}</p>
                        </div>
                      )}
                      {(lesson.testing_done_by || lesson.procedures_written_by || lesson.implement_new_ll_by) && (
                        <div className="grid grid-cols-3 gap-2 mt-2 text-sm bg-gray-800 p-2 rounded">
                          {lesson.testing_done_by && (
                            <div>
                              <p className="text-gray-500 text-xs">Testing Done By:</p>
                              <p className="text-gray-300 text-xs">{lesson.testing_done_by}</p>
                            </div>
                          )}
                          {lesson.procedures_written_by && (
                            <div>
                              <p className="text-gray-500 text-xs">Procedures Written By:</p>
                              <p className="text-gray-300 text-xs">{lesson.procedures_written_by}</p>
                            </div>
                          )}
                          {lesson.implement_new_ll_by && (
                            <div>
                              <p className="text-gray-500 text-xs">Implemented New LL By:</p>
                              <p className="text-gray-300 text-xs">{lesson.implement_new_ll_by}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section V: Improvement Plan and Next Steps */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">V</div>
            <div>
              <CardTitle className="text-blue-400">Improvement Plan and Next Steps</CardTitle>
              <CardDescription className="text-gray-400">
                Actionable recommendations and follow-up activities
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Recommendations
              <span className="text-gray-500 font-normal ml-2">Actionable suggestions for changes to policies, procedures, or training.</span>
            </label>
            <textarea
              className={`${textareaClass} min-h-[120px]`}
              placeholder="Actionable suggestions for changes to policies, procedures, or training."
              value={aarData.improvement_plan.recommendations}
              onChange={(e) => updateSection('improvement_plan', 'recommendations', e.target.value)}
            />
          </div>
          
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Responsible Parties
              <span className="text-gray-500 font-normal ml-2">Assignment of specific individuals or departments responsible for implementing each action item.</span>
            </label>
            <textarea
              className={`${textareaClass} min-h-[120px]`}
              placeholder="Assignment of specific individuals or departments responsible for implementing each action item."
              value={aarData.improvement_plan.responsible_parties}
              onChange={(e) => updateSection('improvement_plan', 'responsible_parties', e.target.value)}
            />
          </div>
          
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Timelines
              <span className="text-gray-500 font-normal ml-2">Expected completion dates for all corrective actions.</span>
            </label>
            <textarea
              className={`${textareaClass} min-h-[120px]`}
              placeholder="Expected completion dates for all corrective actions."
              value={aarData.improvement_plan.timelines}
              onChange={(e) => updateSection('improvement_plan', 'timelines', e.target.value)}
            />
          </div>
          
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Follow-Up Plan
              <span className="text-gray-500 font-normal ml-2">A process for monitoring progress and ensuring recommendations are integrated into future operations.</span>
            </label>
            <textarea
              className={`${textareaClass} min-h-[120px]`}
              placeholder="A process for monitoring progress and ensuring recommendations are integrated into future operations."
              value={aarData.improvement_plan.follow_up_plan}
              onChange={(e) => updateSection('improvement_plan', 'follow_up_plan', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Section VI: Incident Commander Final Comments */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">VI</div>
            <div>
              <CardTitle className="text-blue-400">Incident Commander Final Comments</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          <div>
            <label className={labelClass}>
              <span className="text-blue-400">●</span> Commanders Comments
            </label>
            <textarea
              className={`${textareaClass} min-h-[150px]`}
              placeholder="Enter commanders comments..."
              value={aarData.commander_comments || ''}
              onChange={(e) => setAarData(prev => ({ ...prev, commander_comments: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Annex A: Incident Tracker (ICS 233) */}
      <Card className="bg-gray-800 border-gray-700" data-testid="annex-a-incident-tracker">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-sm">A</div>
            <div>
              <CardTitle className="text-red-400">Annex A: Incident Tracker (ICS 233)</CardTitle>
              <CardDescription className="text-gray-400">
                {(() => {
                  const total = incidentTrackerEntries.reduce((s, e) => s + (e.action_items?.length || 0), 0);
                  return `Action items & tracking entries (${total} total)`;
                })()}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {(() => {
            const allItems = incidentTrackerEntries.flatMap(e => e.action_items || []);
            if (allItems.length === 0) {
              return <p className="text-gray-500 italic">No incident tracker entries for this exercise. Create entries in the Incident Tracker (ICS 233) menu to see them here.</p>;
            }
            const statusBadge = (s) => {
              const v = (s || '').toLowerCase();
              if (v.includes('complete') || v.includes('closed')) return 'bg-green-500/20 text-green-300 border border-green-500/30';
              if (v.includes('progress') || v.includes('open')) return 'bg-amber-500/20 text-amber-300 border border-amber-500/30';
              if (v.includes('overdue') || v.includes('block')) return 'bg-red-500/20 text-red-300 border border-red-500/30';
              return 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
            };
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="incident-tracker-table">
                  <thead>
                    <tr className="border-b border-gray-600">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">#</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Item / Action</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">For POC</th>
                      <th className="text-center py-2 px-3 text-gray-400 font-medium">Briefed</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Start Date</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Target Date</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Actual Date</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item, idx) => (
                      <tr key={`${item.no || ''}-${idx}`} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`incident-tracker-row-${idx}`}>
                        <td className="py-2 px-3 text-gray-500">{item.no || (idx + 1)}</td>
                        <td className="py-2 px-3 text-white font-medium">{item.item || 'N/A'}</td>
                        <td className="py-2 px-3 text-gray-300">{item.for_poc || '-'}</td>
                        <td className="py-2 px-3 text-center text-gray-300">
                          {item.briefed_poc ? <span className="text-green-400">✓</span> : <span className="text-gray-600">—</span>}
                        </td>
                        <td className="py-2 px-3 text-gray-300">{item.start_date || '-'}</td>
                        <td className="py-2 px-3 text-gray-300">{item.target_date || '-'}</td>
                        <td className="py-2 px-3 text-gray-300">{item.actual_date || '-'}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${statusBadge(item.status)}`}>
                            {item.status || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Annex B: Exercise Participants */}
      <Card className="bg-gray-800 border-gray-700" data-testid="annex-b-participants">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">B</div>
            <div>
              <CardTitle className="text-blue-400">Annex B: Exercise Participants</CardTitle>
              <CardDescription className="text-gray-400">
                Personnel assigned to this exercise ({participants.length} total)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {participants.length === 0 ? (
            <p className="text-gray-500 italic">No participants assigned to this exercise.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="participants-table">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">#</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Name</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Organization</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Role</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Position</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Assignment</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Strike Team</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Task Force</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p, idx) => (
                    <tr key={p.id || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`participant-row-${idx}`}>
                      <td className="py-2 px-3 text-gray-500">{idx + 1}</td>
                      <td className="py-2 px-3 text-white font-medium">{p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-300">{p.organization || 'N/A'}</td>
                      <td className="py-2 px-3">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                          p.role === 'exercise_director' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                          p.role === 'exercise_observer' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                          p.role === 'exercise_player' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                          'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                        }`}>
                          {(p.role || 'N/A').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-300">{p.position || p.incidentAssignment || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-300">{p.assignedTo || p.currentAssignedLocation || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-300">{p.strikeTeam || '-'}</td>
                      <td className="py-2 px-3 text-gray-300">{p.taskForce || '-'}</td>
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {p.email && <div>{p.email}</div>}
                        {(p.cellPhone || p.phone) && <div>{p.cellPhone || p.phone}</div>}
                        {!p.email && !p.cellPhone && !p.phone && 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Annex C: Exercise Resources */}
      <Card className="bg-gray-800 border-gray-700" data-testid="annex-c-resources">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">C</div>
            <div>
              <CardTitle className="text-teal-400">Annex C: Exercise Resources</CardTitle>
              <CardDescription className="text-gray-400">
                Resources assigned to this exercise from Res. Management ({resources.length} total)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {resources.length === 0 ? (
            <p className="text-gray-500 italic">No resources assigned to this exercise.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="resources-table">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">#</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Identification</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Type</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Status</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Location</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Strike Team</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Task Force</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.map((r, idx) => (
                    <tr key={r.id || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`resource-row-${idx}`}>
                      <td className="py-2 px-3 text-gray-500">{idx + 1}</td>
                      <td className="py-2 px-3 text-white font-medium">{r.identification || 'N/A'}</td>
                      <td className="py-2 px-3">
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/30">
                          {r.resource_type || 'N/A'}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                          r.status === 'Assigned' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                          r.status === 'Available' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                          r.status === 'In Transit' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                          r.status === 'Out of Service' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                          'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                        }`}>
                          {r.status || 'N/A'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-300">{r.location || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-300">{r.strike_team || '-'}</td>
                      <td className="py-2 px-3 text-gray-300">{r.task_force || '-'}</td>
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {r.contact_person && <div>{r.contact_person}</div>}
                        {r.contact_phone && <div>{r.contact_phone}</div>}
                        {!r.contact_person && !r.contact_phone && 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Annex D: Financials (Cost Unit) */}
      <Card className="bg-gray-800 border-gray-700" data-testid="annex-d-financials">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-sm">D</div>
            <div>
              <CardTitle className="text-green-400">Annex D: Financials (Cost Unit)</CardTitle>
              <CardDescription className="text-gray-400">
                Financial summary from Cost Unit entries ({costUnitEntries.length} entries)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {costUnitEntries.length === 0 ? (
            <p className="text-gray-500 italic">No cost unit entries for this exercise.</p>
          ) : (() => {
            const parseCost = (val) => { const n = parseFloat(String(val || '0').replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };
            const allPersonnel = costUnitEntries.flatMap(e => e.personnel_costs || []);
            const allEquipment = costUnitEntries.flatMap(e => e.equipment_costs || []);
            const allProcurement = costUnitEntries.flatMap(e => e.procurement_contracts || []);
            const allIncident = costUnitEntries.flatMap(e => e.incident_costs || []);
            const totalPersonnel = allPersonnel.reduce((s, i) => s + parseCost(i.total_cost), 0);
            const totalEquipment = allEquipment.reduce((s, i) => s + parseCost(i.total_cost), 0);
            const totalProcurement = allProcurement.reduce((s, i) => s + parseCost(i.contract_value), 0);
            const totalIncident = allIncident.reduce((s, i) => s + parseCost(i.amount), 0);
            const grandTotal = totalPersonnel + totalEquipment + totalProcurement + totalIncident;
            const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const SectionTable = ({ title, color, items, columns, totalValue }) => (
              <div className="border border-gray-700 rounded-lg overflow-hidden mb-4">
                <div className="bg-gray-700/50 px-4 py-3 border-b border-gray-600 flex justify-between items-center">
                  <span className="text-white font-semibold">{title} ({items.length} entries)</span>
                  <span className={`font-bold ${color}`}>{fmt(totalValue)}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-600">
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">#</th>
                        {columns.map((col, i) => (
                          <th key={col.label || `col-${i}`} className="text-left py-2 px-3 text-gray-400 font-medium">{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr><td colSpan={columns.length + 1} className="py-4 px-3 text-gray-500 italic text-center">No entries</td></tr>
                      ) : items.map((item, idx) => (
                        <tr key={item.id || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                          <td className="py-2 px-3 text-gray-500">{idx + 1}</td>
                          {columns.map((col, ci) => (
                            <td key={`cell-${ci}`} className={`py-2 px-3 ${col.bold ? 'text-white font-medium' : 'text-gray-300'}`}>
                              {item[col.key] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );

            return (
              <div className="space-y-2">
                <SectionTable
                  title="Personnel Costs"
                  color="text-green-400"
                  items={allPersonnel}
                  totalValue={totalPersonnel}
                  columns={[
                    { key: 'name', label: 'Name', bold: true },
                    { key: 'position', label: 'Position' },
                    { key: 'agency', label: 'Agency' },
                    { key: 'hours_worked', label: 'Hours' },
                    { key: 'hourly_rate', label: 'Rate' },
                    { key: 'total_cost', label: 'Total', bold: true }
                  ]}
                />
                <SectionTable
                  title="Equipment Costs"
                  color="text-green-400"
                  items={allEquipment}
                  totalValue={totalEquipment}
                  columns={[
                    { key: 'equipment_name', label: 'Equipment', bold: true },
                    { key: 'equipment_type', label: 'Type' },
                    { key: 'quantity', label: 'Qty' },
                    { key: 'daily_rate', label: 'Daily Rate' },
                    { key: 'days_used', label: 'Days' },
                    { key: 'total_cost', label: 'Total', bold: true }
                  ]}
                />
                <SectionTable
                  title="Procurement Contracts"
                  color="text-green-400"
                  items={allProcurement}
                  totalValue={totalProcurement}
                  columns={[
                    { key: 'contract_number', label: 'Contract #' },
                    { key: 'vendor_name', label: 'Vendor', bold: true },
                    { key: 'description', label: 'Description' },
                    { key: 'contract_date', label: 'Date' },
                    { key: 'status', label: 'Status' },
                    { key: 'contract_value', label: 'Value', bold: true }
                  ]}
                />
                <SectionTable
                  title="Incident-Related Costs"
                  color="text-green-400"
                  items={allIncident}
                  totalValue={totalIncident}
                  columns={[
                    { key: 'date', label: 'Date' },
                    { key: 'category', label: 'Category' },
                    { key: 'description', label: 'Description', bold: true },
                    { key: 'approved_by', label: 'Approved By' },
                    { key: 'notes', label: 'Notes' },
                    { key: 'amount', label: 'Amount', bold: true }
                  ]}
                />
                <div className="bg-green-600 text-white px-6 py-4 rounded-lg flex justify-between items-center">
                  <span className="text-lg font-bold">Grand Total</span>
                  <span className="text-2xl font-bold">{fmt(grandTotal)}</span>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Annex E: Activity Log Timeline */}
      <Card className="bg-gray-800 border-gray-700" data-testid="annex-e-activity-log">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-orange-600 flex items-center justify-center text-white font-bold text-sm">E</div>
            <div>
              <CardTitle className="text-orange-400">Annex E: Activity Log Timeline</CardTitle>
              <CardDescription className="text-gray-400">
                Chronological record of all unit activities from the Activity Log (ICS 214)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {activityLogs.length === 0 ? (
            <p className="text-gray-500 italic">No activity logs found for this exercise. Create activity logs in the Activity Log menu to see them here.</p>
          ) : (
            <div className="space-y-4">
              {activityLogs.map((log, logIndex) => (
                <div key={log.id || logIndex} className="border-l-4 border-orange-500 pl-4 pb-4 border-b border-gray-700 last:border-b-0" data-testid={`annex-log-${logIndex}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded">LOG {logIndex + 1}</span>
                    <span className="text-gray-400 text-sm">
                      {log.datePrepared} {log.timePrepared}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                    <div>
                      <span className="text-gray-500">Prepared by:</span>
                      <span className="text-white ml-2">{log.name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Position:</span>
                      <span className="text-white ml-2">{log.icsPosition || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="text-sm mb-2">
                    <span className="text-gray-500">Operating Period:</span>
                    <span className="text-white ml-2">
                      {log.operatingPeriodFrom ? new Date(log.operatingPeriodFrom).toLocaleString() : 'N/A'} - {log.operatingPeriodTo ? new Date(log.operatingPeriodTo).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  {/* Major Events — sorted chronologically */}
                  {log.majorEvents && log.majorEvents.length > 0 && (
                    <div className="mt-3">
                      <h5 className="text-orange-400 text-sm font-semibold mb-2">Major Events:</h5>
                      <div className="space-y-2">
                        {[...log.majorEvents].sort((a, b) => {
                          const da = a.dateTime ? new Date(a.dateTime) : 0;
                          const db = b.dateTime ? new Date(b.dateTime) : 0;
                          return da - db;
                        }).map((event, eventIndex) => (
                          <div key={event.id || eventIndex} className="flex items-start gap-2 border-l-2 border-orange-500 pl-2">
                            <span className="text-gray-400 text-xs whitespace-nowrap">
                              {event.dateTime ? new Date(event.dateTime).toLocaleString() : 'No time'}
                            </span>
                            <span className="text-white text-sm">{event.event || 'No description'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Annex F: Communications Log */}
      <Card className="bg-gray-800 border-gray-700" data-testid="annex-f-comms-log">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-sm">F</div>
            <div>
              <CardTitle className="text-purple-400">Annex F: Communications Log (ICS 309)</CardTitle>
              <CardDescription className="text-gray-400">
                Communications log entries for this exercise ({commsLogs.length} entries, {commsLogs.reduce((sum, e) => sum + (e.message_log?.length || 0), 0)} total messages)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {commsLogs.length === 0 ? (
            <p className="text-gray-500 italic">No communications log entries for this exercise.</p>
          ) : (
            <div className="space-y-6">
              {commsLogs.map((entry, entryIdx) => (
                <div key={entry.id || entryIdx} className="border border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-700/50 px-4 py-3 border-b border-gray-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-semibold">{entry.radio_network_name || 'Entry ' + (entryIdx + 1)}</span>
                        <span className="text-gray-400 text-sm ml-3">Operator: {entry.radio_operator || 'N/A'}</span>
                      </div>
                      <span className="text-gray-400 text-xs">
                        {entry.operational_period_from ? entry.operational_period_from.replace('T', ' ') : 'N/A'} → {entry.operational_period_to ? entry.operational_period_to.replace('T', ' ') : 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid={`comms-log-table-${entryIdx}`}>
                      <thead>
                        <tr className="border-b border-gray-600">
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">#</th>
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">Date / Time</th>
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">From</th>
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">From Msg #</th>
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">To</th>
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">To Msg #</th>
                          <th className="text-left py-2 px-3 text-gray-400 font-medium">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(entry.message_log || []).map((msg, msgIdx) => (
                          <tr key={msg.id || msgIdx} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`comms-msg-${entryIdx}-${msgIdx}`}>
                            <td className="py-2 px-3 text-gray-500">{msgIdx + 1}</td>
                            <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{msg.time ? msg.time.replace('T', ' ') : 'N/A'}</td>
                            <td className="py-2 px-3 text-white font-medium">{msg.from_callsign || 'N/A'}</td>
                            <td className="py-2 px-3 text-gray-300">{msg.from_msg_number || '-'}</td>
                            <td className="py-2 px-3 text-white font-medium">{msg.to_callsign || 'N/A'}</td>
                            <td className="py-2 px-3 text-gray-300">{msg.to_msg_number || '-'}</td>
                            <td className="py-2 px-3 text-gray-300">{msg.message || 'N/A'}</td>
                          </tr>
                        ))}
                        {(entry.message_log || []).length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-4 px-3 text-gray-500 italic text-center">No messages in this log entry.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Save Button */}
      <div className="flex justify-end space-x-3 pt-4">
        <Button 
          variant="outline"
          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          onClick={printReport}
        >
          <Printer className="h-4 w-4 mr-2" />
          Print Report
        </Button>
        <Button 
          className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={saveAARData}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Report
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AfterActionReportSection;
